import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Sparkles, Satellite, Map, Camera, MessageSquare, Bell, Package, Heart, TrendingUp, ScanSearch, Leaf } from 'lucide-react';

/**
 * Carousel scorrevole delle feature AgriNexus.
 * Ogni card: illustrazione (icona + sfondo gradient tematico) + titolo + descrizione breve + link.
 * Mobile: swipe orizzontale + snap. Desktop: pulsanti navigazione.
 */
const FEATURES = [
  {
    to: '/stima-quintali',
    icon: Sparkles,
    title: 'Stima Quintali AI',
    desc: 'Fotografa piante e ricevi la stima produzione in quintali. AI + clima ECMWF + dati ISTAT.',
    badge: 'NUOVO',
    gradient: 'from-terracotta/80 via-terracotta/40 to-transparent',
    accent: 'text-terracotta',
    image: 'https://images.unsplash.com/photo-1590779033100-9f60a05a013d?auto=format&fit=crop&w=800&q=80',
  },
  {
    to: '/esplora',
    icon: ScanSearch,
    title: 'Land Intelligence',
    desc: 'Report AI su ogni terreno: clima, altitudine, colture compatibili, valore percepito.',
    badge: 'AI',
    gradient: 'from-accent/60 via-accent/20 to-transparent',
    accent: 'text-accent-foreground',
    image: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=800&q=80',
  },
  {
    to: '/mappa',
    icon: Map,
    title: 'Mappa Interattiva',
    desc: 'Tutti i terreni geolocalizzati su mappa OSM. Filtra per zona, coltura, prezzo.',
    gradient: 'from-blue-500/60 via-blue-500/20 to-transparent',
    accent: 'text-blue-500',
    image: 'https://images.unsplash.com/photo-1524661135-423995f22d0b?auto=format&fit=crop&w=800&q=80',
  },
  {
    to: '/assistente',
    icon: MessageSquare,
    title: 'Assistente AI Chat',
    desc: 'Fai domande su normative, colture, PAC, adempimenti. Risposte in tempo reale con Claude 4.6.',
    badge: 'CHAT',
    gradient: 'from-purple-500/60 via-purple-500/20 to-transparent',
    accent: 'text-purple-400',
    image: 'https://images.unsplash.com/photo-1655720031554-a929595ffad7?auto=format&fit=crop&w=800&q=80',
  },
  {
    to: '/partite',
    icon: Package,
    title: 'Marketplace Partite',
    desc: 'Vendi frutti, ortaggi e cereali direttamente a ristoratori, grossisti e cooperative.',
    gradient: 'from-orange-500/60 via-orange-500/20 to-transparent',
    accent: 'text-orange-400',
    image: 'https://images.unsplash.com/photo-1610348725531-843dff563e2c?auto=format&fit=crop&w=800&q=80',
  },
  {
    to: '/preferenze',
    icon: Bell,
    title: 'Alert Personalizzati',
    desc: 'Salva una ricerca e ricevi email quando appare un terreno che matcha i tuoi criteri.',
    gradient: 'from-yellow-500/60 via-yellow-500/20 to-transparent',
    accent: 'text-yellow-400',
    image: 'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?auto=format&fit=crop&w=800&q=80',
  },
  {
    to: '/suggeriti',
    icon: Heart,
    title: 'Preferiti & Match',
    desc: 'Salva i tuoi terreni preferiti. Il sistema calcola uno score di compatibilità 0-100.',
    gradient: 'from-pink-500/60 via-pink-500/20 to-transparent',
    accent: 'text-pink-400',
    image: 'https://images.unsplash.com/photo-1464226184884-fa280b87c399?auto=format&fit=crop&w=800&q=80',
  },
  {
    to: '/pubblica',
    icon: Leaf,
    title: 'Pubblica in 5 minuti',
    desc: 'Wizard guidato con AI che ti aiuta a scrivere la descrizione perfetta. Auto-pubblicato subito.',
    gradient: 'from-green-500/60 via-green-500/20 to-transparent',
    accent: 'text-accent',
    image: 'https://images.unsplash.com/photo-1500595046743-cd271d694d30?auto=format&fit=crop&w=800&q=80',
  },
];

export default function FeaturesCarousel() {
  const scrollerRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  const updateArrows = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 8);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  };

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
    };
  }, []);

  const scroll = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector('[data-feature-card]');
    const step = card ? card.getBoundingClientRect().width + 16 : 320;
    el.scrollBy({ left: dir * step * 1.5, behavior: 'smooth' });
  };

  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-24 md:mt-32" data-testid="features-carousel">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta mb-3">Tutto ciò che puoi fare</div>
          <h2 className="font-display text-3xl md:text-5xl tracking-tighter max-w-2xl">Un ecosistema, <span className="italic">tante possibilità.</span></h2>
        </div>
        <div className="hidden md:flex items-center gap-2">
          <button
            onClick={() => scroll(-1)}
            disabled={!canPrev}
            data-testid="features-carousel-prev"
            className="w-11 h-11 rounded-full border border-border flex items-center justify-center hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition"
            aria-label="Scorri indietro"
          >
            <ChevronLeft size={18}/>
          </button>
          <button
            onClick={() => scroll(1)}
            disabled={!canNext}
            data-testid="features-carousel-next"
            className="w-11 h-11 rounded-full border border-border flex items-center justify-center hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition"
            aria-label="Scorri avanti"
          >
            <ChevronRight size={18}/>
          </button>
        </div>
      </div>

      <div
        ref={scrollerRef}
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 -mx-6 lg:-mx-10 px-6 lg:px-10 scrollbar-hide"
        style={{ scrollbarWidth: 'none' }}
      >
        {FEATURES.map((f) => (
          <FeatureCard key={f.title} {...f}/>
        ))}
      </div>

      {/* Mobile dots indicator */}
      <div className="md:hidden mt-4 flex justify-center gap-1.5">
        {FEATURES.map((_, i) => (
          <div key={i} className="w-1.5 h-1.5 rounded-full bg-muted-foreground/30"/>
        ))}
      </div>
    </section>
  );
}

function FeatureCard({ to, icon: Icon, title, desc, badge, gradient, accent, image }) {
  return (
    <Link
      to={to}
      data-feature-card
      data-testid={`feature-card-${title.toLowerCase().replace(/\s/g, '-')}`}
      className="group relative flex-shrink-0 w-[280px] md:w-[320px] snap-start rounded-2xl overflow-hidden border border-border bg-card hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
    >
      {/* Immagine di sfondo */}
      <div className="relative h-[180px] overflow-hidden">
        <img
          src={image}
          alt=""
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
        <div className={`absolute inset-0 bg-gradient-to-t ${gradient}`}/>
        <div className="absolute inset-0 bg-gradient-to-t from-card via-card/50 to-transparent"/>
        {/* Icona flottante */}
        <div className="absolute bottom-4 left-4 w-12 h-12 rounded-full bg-background/95 backdrop-blur flex items-center justify-center shadow-lg">
          <Icon size={22} className={accent} strokeWidth={1.8}/>
        </div>
        {/* Badge */}
        {badge && (
          <div className="absolute top-4 right-4 bg-foreground text-background text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full">
            {badge}
          </div>
        )}
      </div>
      {/* Testo */}
      <div className="p-5">
        <h3 className="font-display text-xl tracking-tight">{title}</h3>
        <p className="mt-2 text-sm text-muted-foreground leading-relaxed line-clamp-3">{desc}</p>
        <div className={`mt-4 text-xs font-medium ${accent} flex items-center gap-1 group-hover:gap-2 transition-all`}>
          Scopri di più <ChevronRight size={14}/>
        </div>
      </div>
    </Link>
  );
}
